<template><div><dt-notice kind="warning" title="Use DtStack in favor of Flex CSS Utilities" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Use the <a href="/components/stack" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">DtStack</a> component for most Flex-based layout implementations. View <a href="/about/whats-new/posts/2025-12-2" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Migrating from Flex CSS Utilities to DtStack</a> for more details.
</dt-notice>
<h2 id="flex" tabindex="-1" class="d-docsite--header-2">Flex <a class="header-anchor d-link d-docsite--link d-link" href="#flex" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The <code v-pre>flex</code> property is a shorthand property for <code v-pre>flex-grow</code>, <code v-pre>flex-shrink</code>, and <code v-pre>flex-basis</code> properties. You can also
control the grow and shrink flex values separately with their own utility classes.</p>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; class=&quot;d-w-1000 d-bar-400 d-bgc-moderate&quot;&gt;
  &lt;div class=&quot;d-fl-none d-p-200 d-ps-relative&quot;&gt;Content cannot flex&lt;/div&gt;
  &lt;div class=&quot;d-fl1 d-p-200 d-bgc-moderate-opaque d-ps-relative&quot;&gt;Text that will flex&lt;/div&gt;
  &lt;div class=&quot;d-fl-none d-p-200 d-ps-relative&quot;&gt;Content cannot flex&lt;/div&gt;
&lt;/dt-stack&gt;'>
<dt-stack direction="row" class="d-w-1000 d-bar-400 d-bgc-moderate">
  <div class="d-fl-none d-p-200 d-ps-relative">Content cannot flex</div>
  <div class="d-fl1 d-p-200 d-bgc-moderate-opaque d-ps-relative">Text that will flex</div>
  <div class="d-fl-none d-p-200 d-ps-relative">Content cannot flex</div>
</dt-stack>
</code-example>
<clamped-table-wrapper class="d-mbe-200">
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-ibs-0">
        <tr>
          <th scope="col" class="d-p-0 d-bbw0 d-w20p"><div class="d-p-200 d-bb d-bbw1">Class</div></th>
          <th scope="col" class="d-p-0 d-bbw0 d-w30p"><div class="d-p-200 d-bb d-bbw1">Output</div></th>
          <th scope="col" class="d-p-0 d-bbw0"><div class="d-p-200 d-bb d-bbw1">Description</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="v in properties[0].values">
          <th scope="row" class="d-code--sm d-docsite-code">.d-fl{{ v }}</th>
          <td class="d-code--sm">flex: {{ v }} auto !important;</td>
          <td>{{ properties[0].description }} {{ v }}, flex-shrink to 1 and flex-basis to auto.</td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">.d-fl-unset</th>
          <td class="d-code--sm">flex: unset;</td>
          <td>Resets the flex value to the initial value (0 1 auto).</td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
<h2 id="flex-grow" tabindex="-1" class="d-docsite--header-2">Flex Grow <a class="header-anchor d-link d-docsite--link d-link" href="#flex-grow" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The <code v-pre>flex-grow</code> sets the flex container’s grow factor relative to the parent's main size. The default value is 0.</p>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; class=&quot;d-w-1000 d-bar-400 d-bgc-moderate&quot;&gt;
  &lt;div class=&quot;d-fl-none d-p-200&quot;&gt;Content cannot flex&lt;/div&gt;
  &lt;div class=&quot;d-fl-grow1 d-p-200 d-bgc-moderate-opaque&quot;&gt;Text that will grow&lt;/div&gt;
  &lt;div class=&quot;d-fl-none d-p-200&quot;&gt;Content cannot flex&lt;/div&gt;
&lt;/dt-stack&gt;'>
<dt-stack direction="row" class="d-w-1000 d-bar-400 d-bgc-moderate">
  <div class="d-fl-none d-p-200">Content cannot flex</div>
  <div class="d-fl-grow1 d-p-200 d-bgc-moderate-opaque">Text that will grow</div>
  <div class="d-fl-none d-p-200">Content cannot flex</div>
</dt-stack>
</code-example>
<clamped-table-wrapper class="d-mbe-200">
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-ibs-0">
        <tr>
          <th scope="col" class="d-p-0 d-bbw0 d-w20p"><div class="d-p-200 d-bb d-bbw1">Class</div></th>
          <th scope="col" class="d-p-0 d-bbw0 d-w30p"><div class="d-p-200 d-bb d-bbw1">Output</div></th>
          <th scope="col" class="d-p-0 d-bbw0"><div class="d-p-200 d-bb d-bbw1">Description</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="v in properties[1].values" valign="baseline">
          <th scope="row" class="d-code--sm d-docsite-code">.d-{{ properties[1].class }}{{ v }}</th>
          <td><dt-text as="code" kind="code" size="100">flex-grow: {{ v }} !important;</dt-text></td>
          <td><dt-text as="p" kind="body" size="200">{{ properties[1].description }} {{ v }}.</dt-text></td>
        </tr>
        <tr valign="baseline">
          <th scope="row" class="d-code--sm d-docsite-code">.d-fl-grow-unset</th>
          <td><dt-text as="code" kind="code" size="100">flex-grow: unset !important;</dt-text></td>
          <td><dt-text as="p" kind="body" size="200">Resets the flex-grow value to the initial value (0).</dt-text></td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
<h2 id="flex-shrink" tabindex="-1" class="d-docsite--header-2">Flex Shrink <a class="header-anchor d-link d-docsite--link d-link" href="#flex-shrink" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The <code v-pre>flex-shrink</code> sets the flex container’s shrink factor relative to the parent's main size. The default value is 1.</p>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; class=&quot;d-bar-400 d-bgc-moderate&quot;&gt;
  &lt;div class=&quot;d-bar-400 d-fl-none d-p-200 d-bgc-moderate-opaque&quot;&gt;Longer text that cannot flex&lt;/div&gt;
  &lt;div class=&quot;d-bar-400 d-fl-shrink1 d-p-200 d-bgc-moderate-opaque&quot;&gt;Text that will shrink even if it causes text to wrap&lt;/div&gt;
  &lt;div class=&quot;d-bar-400 d-fl-none d-p-200 d-bgc-moderate-opaque&quot;&gt;Longer text that cannot flex&lt;/div&gt;
&lt;/dt-stack&gt;'>
<dt-stack direction="row" class="d-bar-400 d-bgc-moderate">
  <div class="d-bar-400 d-fl-none d-p-200 d-bgc-moderate-opaque">Longer text that cannot flex</div>
  <div class="d-bar-400 d-fl-shrink1 d-p-200 d-bgc-moderate-opaque">Text that will shrink even if it causes text to wrap</div>
  <div class="d-bar-400 d-fl-none d-p-200 d-bgc-moderate-opaque">Longer text that cannot flex</div>
</dt-stack>
</code-example>
<clamped-table-wrapper class="d-mbe-200">
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-ibs-0">
        <tr>
          <th scope="col" class="d-p-0 d-bbw0 d-w20p"><div class="d-p-200 d-bb d-bbw1">Class</div></th>
          <th scope="col" class="d-p-0 d-bbw0 d-w30p"><div class="d-p-200 d-bb d-bbw1">Output</div></th>
          <th scope="col" class="d-p-0 d-bbw0"><div class="d-p-200 d-bb d-bbw1">Description</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="v in properties[2].values" valign="baseline">
          <th scope="row" class="d-code--sm d-docsite-code">.d-{{ properties[2].class }}{{ v }}</th>
          <td><dt-text as="code" kind="code" size="100">flex-shrink: {{ v }} !important;</dt-text></td>
          <td><dt-text as="p" kind="body" size="200">{{ properties[2].description }} {{ v }}.</dt-text></td>
        </tr>
        <tr valign="baseline">
          <th scope="row" class="d-code--sm d-docsite-code">.d-fl-shrink-unset</th>
          <td><dt-text as="code" kind="code" size="100">flex-shrink: unset !important;</dt-text></td>
          <td><dt-text as="p" kind="body" size="200">Resets the flex-shrink value to the initial value (1).</dt-text></td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
</div></template>


<script setup>
  import { properties } from '@data/flex.json';
  import ClampedTableWrapper from '@baseComponents/ClampedTableWrapper.vue';
</script>